import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { generateCoupon } from "../services/couponService";
import { Box, Button, TextField, Typography, Paper, Grid, Alert, MenuItem, Select, InputLabel, FormControl } from "@mui/material";

const CouponGenerator = () => {
	const { user } = useAuth();
	const [code, setCode] = useState("");
	const [discount, setDiscount] = useState("");
	const [discountType, setDiscountType] = useState("percentage");
	const [expiryDate, setExpiryDate] = useState("");
	const [maxUses, setMaxUses] = useState("");
	const [userMaxUses, setUserMaxUses] = useState("");
	const [message, setMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validation
		const errors = {};
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (!code || !discount || !expiryDate) {
			setErrorMessage("Please fill in all required fields");
			return;
		}

		if (new Date(expiryDate) < today) {
			errors.expiryDate = "Expiry date must be in the future";
		}

		if (parseFloat(discount) <= 0) {
			errors.discount = "Discount must be positive";
		}

		if (discountType === "percentage" && parseFloat(discount) > 100) {
			errors.discount = "Percentage discount cannot exceed 100%";
		}

		if (maxUses && parseInt(maxUses) <= 0) {
			errors.maxUses = "Max uses must be positive";
		}

		if (userMaxUses && parseInt(userMaxUses) <= 0) {
			errors.userMaxUses = "Max uses per user must be positive";
		}

		if (maxUses && userMaxUses && parseInt(userMaxUses) > parseInt(maxUses)) {
			errors.userMaxUses = "Per-user limit cannot exceed total uses";
		}

		if (Object.keys(errors).length > 0) {
			setErrorMessage(Object.values(errors).join(", "));
			return;
		}

		setLoading(true);
		setMessage("");
		setErrorMessage("");

		try {
			await generateCoupon(
				code,
				parseFloat(discount),
				discountType,
				new Date(expiryDate).toISOString(),
				maxUses ? parseInt(maxUses) : 1,
				userMaxUses ? parseInt(userMaxUses) : 1
			);
			setMessage("Coupon generated successfully!");
			setCode("");
			setDiscount("");
			setDiscountType("percentage");
			setExpiryDate("");
			setMaxUses("");
			setUserMaxUses("");
		} catch (error) {
			setMessage(`Error: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	if (!user) {
		return (
			<Typography variant="h6" color="error">
				Please sign in to generate coupons
			</Typography>
		);
	}

	return (
		<Paper sx={{ p: 3, mt: 2 }}>
			<Typography variant="h5" gutterBottom>
				Generate New Coupon
			</Typography>
			{message && (
				<Alert severity={message.includes("Error") ? "error" : "success"} sx={{ mb: 2 }}>
					{message}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}
			<form onSubmit={handleSubmit}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField fullWidth label="Coupon Code" value={code} onChange={(e) => setCode(e.target.value)} required />
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth>
							<InputLabel>Discount Type</InputLabel>
							<Select value={discountType} onChange={(e) => setDiscountType(e.target.value)} label="Discount Type" required>
								<MenuItem value="percentage">Percentage</MenuItem>
								<MenuItem value="flat">Flat</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							label={discountType === "percentage" ? "Discount Percentage" : "Discount Amount"}
							type="number"
							value={discount}
							onChange={(e) => setDiscount(e.target.value)}
							required
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Expiry Date"
							type="date"
							value={expiryDate}
							onChange={(e) => setExpiryDate(e.target.value)}
							required
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField fullWidth label="Max Uses (optional)" type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Max Uses Per User (optional)"
							type="number"
							value={userMaxUses}
							onChange={(e) => setUserMaxUses(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Button type="submit" variant="contained" color="primary" disabled={loading}>
							{loading ? "Generating..." : "Generate Coupon"}
						</Button>
					</Grid>
				</Grid>
			</form>
		</Paper>
	);
};

export default CouponGenerator;
